import { HttpErrorResponse } from '@angular/common/http';

/**
 * Тексты ошибок сервера по машинному коду (keremet-go docs/ERROR_CODES.md).
 * Сервер оставляет message/error для старых клиентов, но админка показывает свой текст по `code`;
 * английские тексты Laravel («Unauthenticated.», «The … field is required.») не показываем никогда.
 */

type Body = Record<string, unknown>;
type Template = string | ((body: Body) => string | null);

const NUMBER = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 });

function tenge(value: unknown): string {
  return typeof value === 'number' ? `${NUMBER.format(value)} ₸` : '';
}

function params(body: Body): Body {
  const value = body['params'];
  return typeof value === 'object' && value !== null ? (value as Body) : {};
}

function nameOf(value: unknown): string | null {
  if (typeof value === 'object' && value !== null && typeof (value as Body)['name'] === 'string') {
    return (value as Body)['name'] as string;
  }
  return null;
}

const DISH_REASONS: Record<string, (name: string) => string> = {
  not_found: () => 'Блюдо больше не продаётся',
  not_available: name => `«${name}» сейчас недоступно`,
  not_on_date: name => `«${name}» не готовим в этот день`,
  corporate: name => `«${name}» недоступно для вашей компании`,
  group: name => `«${name}» недоступно для вашей компании`,
};

const UPDATE_APP = 'Не удалось выполнить действие. Обновите страницу';
const SECTION_MOVED = 'Раздел обновился. Обновите страницу';
const UNAVAILABLE = 'Сервис временно недоступен';
const UUID_TAKEN = 'Действие уже записано другим устройством';

export const ERROR_CODE_TEXTS: Record<string, Template> = {
  // Общие
  unauthenticated: 'Сессия истекла — войдите снова',
  forbidden: 'Недостаточно прав для этого действия',
  not_found: 'Не найдено',
  server_error: 'Ошибка сервера. Попробуйте позже',
  unavailable: UNAVAILABLE,
  undershipment_unavailable: UNAVAILABLE,
  media_unavailable: 'Хранилище фото недоступно',
  stale_version: 'Данные уже изменились. Обновите и повторите',
  idempotency_in_progress: 'Действие ещё выполняется, подождите',
  idempotency_key_required: UPDATE_APP,
  idempotency_key_reused: UPDATE_APP,
  idempotency_response_not_stored: UPDATE_APP,
  bad_header: UPDATE_APP,
  payload_too_large: 'Слишком большой запрос',
  too_many_requests: 'Слишком много запросов. Подождите немного',
  bad_cursor: UPDATE_APP,
  cursor_expired: UPDATE_APP,
  unknown_stage: UPDATE_APP,
  not_implemented: 'Функция ещё не готова',
  uuid_taken: UUID_TAKEN,
  uuid_conflict: UUID_TAKEN,
  session_conflict: UUID_TAKEN,
  password_change_required: 'Смените пароль, чтобы продолжить',
  use_order_edit: SECTION_MOVED,
  use_order_minimums: SECTION_MOVED,
  use_undershipments: SECTION_MOVED,
  plans_replaced: SECTION_MOVED,
  ingredients_replaced: SECTION_MOVED,
  use_techcards: SECTION_MOVED,
  station_off: SECTION_MOVED,

  // Вход и пароль
  invalid_credentials: 'Неверный логин или пароль',
  account_inactive: 'Аккаунт отключён. Обратитесь к администратору',
  role_missing: 'Аккаунту не назначена роль. Обратитесь к администратору',
  login_locked: body => {
    const minutes = params(body)['minutes'];
    return `Слишком много попыток входа. Повторите через ${typeof minutes === 'number' ? minutes : 15} мин`;
  },
  current_password_wrong: 'Текущий пароль указан неверно',
  password_same: 'Новый пароль должен отличаться от текущего',
  client_password_generated: 'Пароль клиенту создаёт система',
  superadmin_only: 'Данные суперадминистратора меняет только суперадминистратор',

  // Заказы
  order_date_closed: 'Приём заказов на эту дату закрыт',
  ORDERING_CLOSED: 'Меню на эту дату закрыто',
  order_exists: 'На эту точку уже есть заказ на эту дату',
  order_already_sent: 'На эту точку уже отправлен заказ на эту дату. Чтобы изменить его, свяжитесь с менеджером',
  order_sent_locked: 'Отправленный заказ изменить нельзя',
  foreign_division: 'Эта точка не относится к вашей компании',
  min_order_sum: body => {
    const p = { ...body, ...params(body) };
    if (typeof p['min_amount'] !== 'number') return null;
    return `Минимальная сумма заказа — ${tenge(p['min_amount'])}, не хватает ${tenge(p['shortage'] ?? 0)}`;
  },
  dish_unavailable: body => {
    const value = params(body)['dishes'];
    const dishes: Body[] = Array.isArray(value) ? value : [];
    const first = dishes[0];
    if (!first) return 'Некоторые блюда недоступны';
    const reason = DISH_REASONS[String(first['reason'])] ?? DISH_REASONS['not_available'];
    const text = reason(String(first['name'] ?? ''));
    return dishes.length > 1 ? `${text} (и ещё ${dishes.length - 1})` : text;
  },
  price_changed: 'Цены изменились. Проверьте заказ и отправьте ещё раз',
  order_not_sent: 'Заказ ещё не отправлен',
  order_packed: 'Заказ уже собран — изменить нельзя. Если чего-то не хватает, оформите недовоз',
  day_closed: 'День производства закрыт',
  order_sent_header: 'У отправленного заказа филиал и дату не меняют — распроведите заказ',
  order_empty: 'В заказе должна остаться хотя бы одна позиция',

  // Коины (bonus_error_code)
  coins_order_not_sent: 'Бонусы списываются только при отправке заказа',
  coins_program_off: 'Бонусная программа временно отключена',
  coins_not_on_date: body => {
    const days = (body['bonus_error_params'] as Body | undefined)?.['days'] ?? params(body)['days'];
    return Array.isArray(days) && days.length
      ? `Бонусы можно списывать только ${days.join(', ')} числа`
      : 'На эту дату отгрузки бонусы не списываются';
  },
  coins_contragent_not_found: 'Компания не найдена',
  coins_corporate: 'Корпоративным клиентам бонусы не начисляются и не списываются',
  coins_group_off: 'Бонусная программа недоступна для вашей группы клиентов',
  coins_contragent_off: 'Для вашей компании бонусная программа отключена',
  coins_no_balance: 'На балансе нет бонусов',
  coins_zero_amount: 'В этом заказе нечего оплатить бонусами',
  coins_not_with_promo: 'С этим промокодом бонусы не списываются',

  // Промокоды
  promo_expired: 'Промокод больше не действует',
  promo_min_order: body => {
    const p = (body['reason_params'] as Body | undefined) ?? params(body);
    return typeof p['min_order'] === 'number'
      ? `Промокод действует от ${tenge(p['min_order'])} — добавьте ещё на ${tenge(p['shortage'] ?? 0)}`
      : 'Сумма заказа меньше минимальной для промокода';
  },
  promo_promotion_dishes_only: 'Промокод не действует на блюда по акции — добавьте другие блюда',
  promo_invalid: 'Промокод не подходит',
  promo_disabled: 'Промокоды временно недоступны',
  promo_rate_limited: 'Слишком много попыток. Попробуйте позже',

  // Курьер и доставка
  not_a_courier: 'Учётная запись не привязана к курьеру',
  not_your_route: 'Эта доставка не в вашем маршруте',
  label_mismatch: 'Этикетка от другого заказа',
  bags_mismatch: body =>
    typeof body['bags_expected'] === 'number' ? `В уголке ${body['bags_expected']} пак. — пересчитайте` : 'Число пакетов не сходится — пересчитайте',
  photo_required: 'Нужно фото',
  delivery_closed: 'Заказ уже доставлен',
  stage_regression: 'Заказ уже на следующем этапе',
  review_not_needed: 'Доставку уже проверили автоматически',
  delivery_mode_locked: 'Заказ уже у курьера или выдан — способ получения не сменить',
  user_taken: body => {
    const name = body['courier_name'] ?? params(body)['courier_name'];
    return typeof name === 'string' && name ? `Пользователь уже привязан к курьеру «${name}»` : 'Пользователь уже привязан к другому курьеру';
  },
  shift_not_started: 'Смена не идёт',

  // Фасовка
  already_packed: 'Заказ уже собран',
  taken_by_other: body => {
    const name = nameOf(body['packer']);
    return name ? `Сборку уже взяла ${name}` : 'Сборку уже взяла другая фасовщица';
  },
  not_packed: 'Заказ ещё не собран',
  qty_exceeds_count: 'Количество больше, чем в заказе',
  checklist_incomplete: 'Отметьте все позиции: «положила» или «нет позиции»',
  not_pickup: 'Заказ не на самовывоз — его везёт курьер',

  // Кухня, станции, планшеты, техкарты
  day_not_frozen: 'План на этот день ещё не утверждён',
  day_frozen: 'День уже заморожен — продлить приём нельзя',
  shortage_not_open: 'Нехватку уже подтвердили или отменили',
  not_your_station: 'Это задание другой станции',
  not_batch_semi: 'Этот полуфабрикат готовит другая станция',
  allocation_mismatch: 'Распределение не сходится с количеством. Обновите и повторите',
  employee_not_allowed: 'Этот сотрудник не может работать на планшете станции',
  pin_invalid: body =>
    typeof body['attempts_left'] === 'number' ? `Неверный PIN. Осталось попыток: ${body['attempts_left']}` : 'Неверный PIN',
  pin_locked: 'Слишком много неверных PIN. Попробуйте позже или попросите старшего смены сменить PIN',
  device_revoked: 'Планшет отключён. Обратитесь к администратору',
  enrollment_invalid: 'Код регистрации неверный или устарел. Получите новый в разделе «Планшеты»',
  station_has_devices: 'К станции привязан планшет. Сначала отзовите его',
  station_in_use: 'Станция используется — сначала уберите её из блюд и техкарт',
  station_has_dishes: 'Станция используется — сначала уберите её из блюд и техкарт',
  not_semi: 'Позиция не полуфабрикат',
  recipe_cycle: body =>
    Array.isArray(body['path']) ? `Техкарта ссылается сама на себя: ${(body['path'] as unknown[]).join(' → ')}` : 'Техкарта ссылается сама на себя',
  recipe_too_deep: body =>
    typeof body['max_depth'] === 'number'
      ? `Слишком глубокая вложенность полуфабрикатов (больше ${body['max_depth']})`
      : 'Слишком глубокая вложенность полуфабрикатов',

  // Склад, недовозы, фото, справочники
  barcode_unknown: 'Штрихкод не найден. Привяжите его к позиции',
  barcode_taken: body => {
    const name = nameOf(body['item']);
    return name ? `Штрихкод уже привязан к «${name}»` : 'Штрихкод уже привязан к другой позиции';
  },
  barcode_missing: 'У позиции нет штрихкода для этикетки. Создайте внутренний код',
  receipt_reversed: 'Приход уже сторнирован',
  supplier_required: 'Укажите поставщика',
  qty_mismatch: 'Количество не равно числу упаковок × размер упаковки',
  price_required: 'Укажите цену за единицу в каждой строке',
  claim_window_closed: 'Сообщить о недовозе можно до конца следующего дня после доставки',
  undershipment_not_claimed: 'Заявку уже рассмотрели',
  order_not_delivered: 'Заказ ещё не доставлен',
  photo_too_large: 'Фото больше 3 МБ',
  upload_missing: 'Фото не загрузилось. Повторите',
  upload_changed: 'Фото не загрузилось. Повторите',
  upload_mismatch: 'Фото не загрузилось. Повторите',
  system_group: 'Системную группу удалить нельзя',
  system_level: 'Уровень «Без минимума» удалить нельзя',
  level_in_use: 'Уровень назначен точкам. Сначала переведите их на другой уровень',
  system_price_list: 'Системный прайс-лист удалить нельзя',
  price_list_in_use: 'Прайс-лист используется. Сначала снимите назначения',
  self_approval: 'Подтвердить должен другой старший менеджер',
};

/** Правила валидации старых роутов (`field_codes`: «required», «min:8», …). */
function ruleText(rule: string): string {
  const [name, arg] = rule.split(':', 2);
  const known = ERROR_CODE_TEXTS[name];
  if (typeof known === 'string') return known;
  switch (name) {
    case 'required':
    case 'present':
    case 'required_with':
      return 'Заполните поле';
    case 'min':
      return arg ? `Не короче ${arg} символов` : 'Слишком короткое значение';
    case 'max':
      return arg ? `Не длиннее ${arg} символов` : 'Слишком длинное значение';
    case 'confirmed':
      return 'Пароли не совпадают';
    case 'unique':
      return 'Такое значение уже есть';
    case 'email':
      return 'Неверный адрес почты';
    default:
      return 'Неверное значение';
  }
}

/** Английский текст сервера (Laravel) — без кириллицы; такой не показываем. */
export function isEnglishText(text: string): boolean {
  return !/[А-Яа-яЁёӘәҒғҚқҢңӨөҰұҮүІіҺһ]/.test(text);
}

function russian(value: unknown): string | null {
  return typeof value === 'string' && value.trim() && !isEnglishText(value) ? value : null;
}

const GENERIC_VALIDATION = new Set(['Ошибка валидации', 'Ошибка валидации.']);

function firstFieldText(body: Body): string | null {
  const fieldCodes = body['field_codes'];
  if (typeof fieldCodes === 'object' && fieldCodes !== null) {
    for (const rules of Object.values(fieldCodes as Record<string, unknown>)) {
      if (Array.isArray(rules) && typeof rules[0] === 'string') return ruleText(rules[0]);
    }
  }
  const errors = body['errors'];
  if (typeof errors === 'object' && errors !== null) {
    for (const value of Object.values(errors as Record<string, unknown>)) {
      const first = Array.isArray(value) ? value[0] : value;
      const text = russian(first);
      if (text) return text;
    }
  }
  return null;
}

/** Текст по коду ответа (кроме validation_failed); null — код неизвестен или без кода. */
export function errorCodeText(body: unknown, code?: string | null): string | null {
  if (typeof body !== 'object' || body === null) return null;
  const key = code ?? (body as Body)['code'];
  if (typeof key !== 'string' || key === 'validation_failed') return null;
  const template = ERROR_CODE_TEXTS[key];
  if (template === undefined) return null;
  return typeof template === 'string' ? template : template(body as Body);
}

const STATUS_TEXTS: Record<number, string> = {
  401: 'Сессия истекла — войдите снова',
  403: 'Недостаточно прав для этого действия',
  422: 'Проверьте введённые данные',
  429: 'Слишком много попыток. Попробуйте позже',
};

/**
 * Русский текст ошибки тела ответа: код → текст реестра; 422 → правило/ошибка поля;
 * иначе русский message/error сервера; английский — общий текст по статусу; null — текста нет.
 */
export function serverErrorText(body: unknown, status: number): string | null {
  if (typeof body === 'object' && body !== null) {
    const data = body as Body;
    const byCode = errorCodeText(data);
    if (byCode) return byCode;
    if (data['code'] === 'validation_failed' || status === 422) {
      const field = firstFieldText(data);
      if (field) return field;
      const message = russian(data['message']);
      if (message && !GENERIC_VALIDATION.has(message)) return message;
      return STATUS_TEXTS[422];
    }
    const message = russian(data['message']) ?? russian(data['error']);
    if (message) return message;
  } else if (russian(body)) {
    return body as string;
  }
  return STATUS_TEXTS[status] ?? null;
}

/** Текст ошибки HTTP-запроса для тоста: см. serverErrorText; нет текста — fallback. */
export function apiErrorText(error: unknown, fallback: string): string {
  if (error instanceof HttpErrorResponse) {
    if (error.status === 0) return 'Нет связи с сервером API (сеть или CORS).';
    return serverErrorText(error.error, error.status) ?? fallback;
  }
  const maybe = error as { error?: unknown; status?: unknown } | null;
  if (maybe && typeof maybe === 'object' && 'error' in maybe) {
    return serverErrorText(maybe.error, typeof maybe.status === 'number' ? maybe.status : 0) ?? fallback;
  }
  return fallback;
}
